import {
    AccountInfo,
    AuthenticationResult,
    BrowserCacheLocation,
    InteractionRequiredAuthError,
    IPublicClientApplication,
    PublicClientApplication,
} from "@azure/msal-browser";
import { getUserFromJwt, SignedInUser } from "./services/JwtUser";

export type { SignedInUser } from "./services/JwtUser";

// Fallback sign-in used when the app runs in a plain browser (outside Teams) for development and testing.
// It reuses the user's existing Entra ID session (e.g. from the App Proxy pre-authentication) through MSAL.

const clientId = process.env.REACT_APP_AAD_CLIENT_ID;
const tenantId = process.env.REACT_APP_AAD_TENANT_ID || "organizations";
const apiScope = process.env.REACT_APP_API_SCOPE;

const configuredRedirectUri = process.env.REACT_APP_AAD_REDIRECT_URI;

const loginScopes = ["openid", "profile", "email"];

let msalInstancePromise: Promise<IPublicClientApplication> | null = null;
let redirectSignInFailed = false;

export const isBrowserAuthConfigured = (): boolean => Boolean(clientId);

// MSAL responses always carry "state" next to "code"/"error", which avoids matching app routes by accident.
const isAuthResponseInUrl = (): boolean => {
    const url = window.location.hash + window.location.search;
    return /[#?&](code|error)=/.test(url) && /[#?&]state=/.test(url);
};

/**
 * The redirect URI must exactly match a "Single-page application" redirect URI on the app registration.
 * Defaults to the URL the app is served from, e.g. https://howru-dev.secotools.com/howruweb
 * (Entra treats a trailing slash on a path as significant, so it is removed).
 */
export const getRedirectUri = (): string => {
    if (configuredRedirectUri) {
        return configuredRedirectUri;
    }

    const basePath = window.location.pathname.replace(/\/index\.html$/i, "").replace(/\/+$/, "");
    return `${window.location.origin}${basePath || "/"}`;
};

/**
 * Silent sign-in loads the redirect URI (the app itself) in a hidden iframe. MSAL reads the response
 * from the iframe URL, so the app must not boot there.
 */
export const isSilentSignInFrame = (): boolean => {
    // Teams also hosts the app in an iframe, so only treat the frame as a sign-in frame when browser auth is in use.
    if (!isBrowserAuthConfigured()) {
        return false;
    }

    let inIframe: boolean;
    try {
        inIframe = window.self !== window.top;
    } catch {
        inIframe = true;
    }

    return inIframe && isAuthResponseInUrl();
};

const getMsalInstance = (): Promise<IPublicClientApplication> => {
    if (!msalInstancePromise) {
        msalInstancePromise = PublicClientApplication.createPublicClientApplication({
            auth: {
                clientId: clientId!,
                authority: `https://login.microsoftonline.com/${tenantId}`,
                redirectUri: getRedirectUri(),
                // The app uses hash routing, so the original route is restored from the request state instead.
                navigateToLoginRequestUrl: false,
            },
            cache: {
                cacheLocation: BrowserCacheLocation.SessionStorage,
            },
        });
    }

    return msalInstancePromise;
};

const restoreRouteFromState = (result: AuthenticationResult | null): void => {
    const route = result?.state;
    if (route && route.startsWith("#/")) {
        window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${route}`);
    }
};

/**
 * Completes a pending browser sign-in redirect. Must run before the hash router renders,
 * otherwise the router rewrites the authorization response in the URL hash.
 */
export const completeBrowserSignInRedirect = async (): Promise<void> => {
    if (!isBrowserAuthConfigured() || !isAuthResponseInUrl()) {
        return;
    }

    try {
        const instance = await getMsalInstance();
        const result = await instance.handleRedirectPromise();
        if (result?.account) {
            instance.setActiveAccount(result.account);
        }
        restoreRouteFromState(result);
    } catch (error) {
        redirectSignInFailed = true;
        console.error("Browser sign-in redirect failed:", error);
    }
};

const toSignedInUser = (account: AccountInfo): SignedInUser => {
    const idTokenUser = getUserFromJwt(account.idToken);
    if (idTokenUser) {
        return idTokenUser;
    }

    const upn = account.idTokenClaims?.upn;

    return {
        // localAccountId is the Entra object id, which matches the Teams context user id.
        id: account.localAccountId,
        email: typeof upn === "string" && upn ? upn : account.username,
        displayName: account.name ?? account.username,
    };
};

const findSignedInAccount = async (instance: IPublicClientApplication): Promise<AccountInfo | null> => {
    const existingAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
    if (existingAccount) {
        return existingAccount;
    }

    try {
        // Reuses the existing Entra ID session without user interaction.
        const result = await instance.ssoSilent({
            scopes: loginScopes,
        });
        return result.account;
    } catch (error) {
        if (redirectSignInFailed) {
            // Avoid a redirect loop when the previous interactive sign-in already failed.
            console.error("Silent browser sign-in failed after a failed interactive sign-in.", error);
            return null;
        }

        console.info("Silent browser sign-in failed, redirecting to interactive sign-in.", error);
        await instance.loginRedirect({
            scopes: loginScopes,
            state: window.location.hash,
        });
        return null;
    }
};

/**
 * Returns an access token for the HowRU API when REACT_APP_API_SCOPE is configured.
 * Without a scope, API calls keep relying on the existing browser session.
 */
export const getBrowserAccessToken = async (): Promise<string | null> => {
    if (!isBrowserAuthConfigured() || !apiScope) {
        return null;
    }

    const instance = await getMsalInstance();
    const account = instance.getActiveAccount();
    if (!account) {
        return null;
    }

    try {
        const result = await instance.acquireTokenSilent({
            scopes: [apiScope],
            account,
        });
        return result.accessToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError && !redirectSignInFailed) {
            // Typically a missing consent for the API scope; resolved once interactively.
            try {
                await instance.acquireTokenRedirect({
                    scopes: [apiScope],
                    account,
                    state: window.location.hash,
                });
            } catch (redirectError) {
                console.error("Failed to start interactive API token request:", redirectError);
            }
            return null;
        }

        console.error("Failed to acquire API token in browser:", error);
        return null;
    }
};

/**
 * Resolves the signed-in user when running outside Teams.
 * Returns null when browser sign-in is not configured or an interactive redirect was started.
 */
export const signInWithBrowser = async (): Promise<SignedInUser | null> => {
    if (!isBrowserAuthConfigured()) {
        console.warn("Not running in Teams and REACT_APP_AAD_CLIENT_ID is not set; the user identity is unavailable.");
        return null;
    }

    try {
        const instance = await getMsalInstance();
        const account = await findSignedInAccount(instance);
        if (!account) {
            return null;
        }

        instance.setActiveAccount(account);
        return toSignedInUser(account);
    } catch (error) {
        console.error("Browser sign-in failed:", error);
        return null;
    }
};
